import { useMemo } from "react";

import { isServiceInterfacePlatform } from "@/constants/service-interface-platform-catalog";
import { isCustomerPlatformBindingActive } from "@/helpers/customer-platform-binding.helpers";
import useGetCustomerPlatforms from "@/hooks/requests/customer-platform/use-get-customer-platforms";
import useGetAllPlatforms from "@/hooks/requests/platform/use-get-all-platform";
import useGetCustomerId from "@/hooks/use-get-customer-id";

export type IntegrationPlatformOption = {
  platformId: number;
  name: string;
  displayName: string;
};

const useIntegrationFilterOptions = () => {
  const customerId = useGetCustomerId();
  const { data: customerPlatforms } = useGetCustomerPlatforms({
    customerId,
  });
  const { data: platformsPage } = useGetAllPlatforms({
    pageSize: 100,
  });

  const platformOptions = useMemo((): IntegrationPlatformOption[] => {
    if (!customerPlatforms?.length || !platformsPage?.items?.length) return [];

    const platformById = new Map(platformsPage.items.map((p) => [p.id, p]));
    const seen = new Set<number>();
    const options: IntegrationPlatformOption[] = [];

    for (const cp of customerPlatforms) {
      if (!isCustomerPlatformBindingActive(cp) || seen.has(cp.platformId)) continue;

      const platform = platformById.get(cp.platformId);
      if (!platform || !isServiceInterfacePlatform(platform.name)) continue;

      seen.add(cp.platformId);
      options.push({
        platformId: platform.id,
        name: platform.name,
        displayName: platform.displayName || platform.name,
      });
    }

    return options.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [customerPlatforms, platformsPage?.items]);

  return { platformOptions };
};

export default useIntegrationFilterOptions;
